import { screenSize, theme } from "@src/theme";
import React, { useState } from "react";
import styled from "styled-components";
import TopBarButton from "./TopBarButton";
import Logo from "../Logo";
import GreetingText from "./GreetingText";
import UserSVG from "@src/assets/img/user-avatar.svg";
import MessagesTopBarIcon from "@src/assets/img/messages-topbar-icon.svg";
import MessagesTopBarBadge from "@src/assets/img/messages-topbar-badge.svg";
import MenuSVG from "@src/assets/img/menu-icon.svg";
import { useLocation, useNavigate } from "react-router-dom";
import { useSettings } from "@src/context/SettingsContext";
import { useAppDispatch } from "@src/redux/hooks";
import { logoutThunk } from "@app-redux/thunks/authThunks";
import Tooltip from "../Tooltip";
import useModal from "@src/hooks/useModal";
import UIModal from "../UIModal/UIModal";
import { UIButton } from "../UIButton";
import DesktopDownloadButton from "../DesktopDownloadButton";
import { useAuthorizedRoutes } from "@src/hooks/useAuthorizedRoutes";
import { renderRouteIcon } from "@src/utils/renderRouteIcon";
import { useQuery } from "@tanstack/react-query";
import { useAuthCurator } from "@src/hooks/useAuth";
import { getCuratorConversations } from "@src/apis/curatorConversationApis";
import { useCuratorConversationsContext } from "@src/context/CuratorConversationsContext";
import { CONVERSATIONS_POLL_INTERVAL_MS } from "@src/constants/ivsChat";
import {
  formatMessagesBadgeCount,
  getTotalPendingMessagesCount,
} from "@src/pages/Messages/conversationUtils";

const TopBarContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: ${screenSize.desktopXL};
  height: 100%;
  padding-left: ${theme.margins.xl};
  padding-right: ${theme.margins.xl};
`;
const LeftContent = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: ${theme.margins.medium};
  height: 51px;
  @media (max-width: 1160px) {
    display: none;
  }
`;
const ShowOnMobile = styled.div`
  display: none;
  min-width: 33%;
  @media (max-width: 1160px) {
    display: block;
  }
`;
const CenterContent = styled.div`
  flex: 1;
  justify-content: center;
  align-items: center;
  display: flex;
  align-items: center;
  height: 51px;
`;
const RightContent = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${theme.margins.medium};
  height: 51px;
`;

const UserActionsGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  height: 51px;
`;
const DropdownMenu = styled.div<{ $showMenu: boolean }>`
  position: absolute;
  right: 25px;
  top: 55px;
  background-color: ${theme.colors.darkerBg};
  border: 1px solid ${theme.colors.yellow};
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  z-index: 100;
  width: 150px;
  padding: 5px;
  display: flex;
  flex-direction: column;
  align-items: center;
  color: ${theme.colors.black};
  display: ${(props): string => (props.$showMenu ? "block" : "none")};
  transition: all 0.3s ease-in-out;
`;
const DropdownButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: ${theme.colors.white};
  font-size: 1rem;
  padding: 15px 0;
  width: 100%;
  &:hover {
    color: ${theme.colors.yellow};
    background-color: ${theme.colors.darkBg};
  }
  transition: all 0.3s ease-in-out;
  justify-content: center;
  align-items: center;
  display: flex;
`;

const TopBarIconWithBadge = styled.div`
  position: relative;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const MessagesIconButton = styled.button`
  width: 28px;
  height: 28px;
  border: none;
  background: transparent;
  padding: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  outline: none;

  &:focus {
    outline: none;
  }
`;

const MessageBadge = styled.div`
  position: absolute;
  top: -3px;
  right: -5px;
  width: 16px;
  height: 16px;
  pointer-events: none;
  z-index: 2;
`;

const MessageBadgeLabel = styled.span`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${theme.colors.black};
  font-size: 0.55rem;
  font-weight: 700;
  line-height: 1;
`;

const DashboardTopBar = (): React.JSX.Element => {
  const location = useLocation();
  const [isUserDropdownOpen, setUserDropdownOpen] = useState<boolean>(false);
  const { toggleSideNavMenu } = useSettings();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const routes = useAuthorizedRoutes();
  const curator = useAuthCurator();
  const { isEventsConnected } = useCuratorConversationsContext();
  const hasMessagesRoute = routes.some((route) => route.path === "/messages");

  const { data: conversations = [] } = useQuery({
    queryKey: ["conversations"],
    queryFn: () => getCuratorConversations(),
    enabled: !!curator?.documentId && hasMessagesRoute,
    refetchInterval: isEventsConnected ? false : CONVERSATIONS_POLL_INTERVAL_MS,
    refetchOnWindowFocus: true,
  });

  const pendingMessagesBadge = formatMessagesBadgeCount(
    getTotalPendingMessagesCount(conversations)
  );

  const onRouteClick = (route: string): void => {
    navigate(route);
  };
  const toggleDropdown = (): void => {
    setUserDropdownOpen(!isUserDropdownOpen);
  };
  const { closeModal, isOpen, openModal } = useModal();

  return (
    <div className="top-bar-dashboard">
      <UIModal
        isOpen={isOpen}
        onClose={closeModal}
        title="Are you sure you want to sign out?"
        actions={[
          <UIButton
            key="cancel"
            onClick={() => closeModal()}
            className="secondary"
          >
            Cancel
          </UIButton>,
          <UIButton
            key="sign-out"
            onClick={() => dispatch(logoutThunk())}
            variant="dangerOutline"
          >
            Confirm
          </UIButton>,
        ]}
      >
        <p>You will be redirected to the login page.</p>
      </UIModal>
      <TopBarContent>
        <ShowOnMobile>
          <TopBarButton onClick={toggleSideNavMenu} color={theme.colors.yellow}>
            <img
              src={MenuSVG}
              alt="Menu"
              style={{
                width: "100%",
                objectFit: "contain",
                userSelect: "none",
              }}
            />
          </TopBarButton>
        </ShowOnMobile>
        <LeftContent>
          {routes
            .filter((route) => route.path !== "/messages")
            .map((route) => {
              return (
                <TopBarButton
                  key={route.path}
                  tooltipContent={route.title}
                  onClick={(): void => onRouteClick(route.path)}
                  color={
                    location.pathname === route.path
                      ? theme.colors.black
                      : theme.colors.yellow
                  }
                >
                  {renderRouteIcon({
                    route: route.path,
                    active: location.pathname === route.path,
                    title: route.title,
                  })}
                </TopBarButton>
              );
            })}
        </LeftContent>
        <CenterContent>
          <Logo />
        </CenterContent>
        <RightContent>
          <DesktopDownloadButton />
          <UserActionsGroup>
            <GreetingText />
            {hasMessagesRoute && (
              <Tooltip content="Messages" position="bottom">
                <TopBarIconWithBadge>
                  <MessagesIconButton
                    type="button"
                    onClick={(): void => onRouteClick("/messages")}
                    aria-label="Messages"
                  >
                    <img
                      src={MessagesTopBarIcon}
                      alt=""
                      width={28}
                      height={28}
                      style={{ display: "block", userSelect: "none" }}
                    />
                  </MessagesIconButton>
                  {pendingMessagesBadge && (
                    <MessageBadge aria-hidden>
                      <img
                        src={MessagesTopBarBadge}
                        alt=""
                        width={16}
                        height={16}
                        style={{ display: "block" }}
                      />
                      <MessageBadgeLabel>
                        {pendingMessagesBadge}
                      </MessageBadgeLabel>
                    </MessageBadge>
                  )}
                </TopBarIconWithBadge>
              </Tooltip>
            )}
            <TopBarButton onClick={toggleDropdown}>
              <img
                src={UserSVG}
                alt="User"
                style={{
                  width: "80%",
                  objectFit: "contain",
                  userSelect: "none",
                }}
              />
            </TopBarButton>
          </UserActionsGroup>
          <DropdownMenu $showMenu={isUserDropdownOpen}>
            <DropdownButton onClick={() => openModal()}>
              <Tooltip position="top" content="Close session">
                Sign out
              </Tooltip>
            </DropdownButton>
          </DropdownMenu>
        </RightContent>
      </TopBarContent>
    </div>
  );
};

export default DashboardTopBar;
